/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useWeb3Auth } from '../../providers/Web3AuthProvider';
import {
	getCombinedKarmaBalance,
	getEthBalance,
	getKusdBalance,
	formatCurrency,
	KUSD_TOKEN_ADDRESS,
} from '../../utils/wallet-helpers';
import { ReceiveTokensModal } from './ReceiveTokensModal';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
	ExternalLink,
	RefreshCw,
	Wallet,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { MemberReadProvider } from '@/lib/member-wallet';
import { ethers } from 'ethers';
import {
	BASE_WKRM_ADDRESS,
	WATCHER_BUILDER_CONTRACT_ADDRESS,
	WATCHER_FOUNDER_PASS_CONTRACT_ADDRESS,
	WATCHER_GAME_ID_CONTRACT_ADDRESS,
	WATCHER_ORACLE_EDITION_CONTRACT_ADDRESS,
	WATCHER_PLAYER_CONTRACT_ADDRESS,
} from '@/lib/watcher-contracts';

// Component props
interface WalletCardProps {
	className?: string;
}

// Token address from env var for balance display
const tokenAddress = import.meta.env.VITE_PREDICT_TOKEN_ADDRESS;

// Copy to clipboard helper function
const copyToClipboard = (text: string, label: string) => {
	navigator.clipboard.writeText(text);
	toast({
		title: 'Copied!',
		description: `${label} copied to clipboard`,
		duration: 2000,
	});
};

export function WalletCard({ className = '' }: WalletCardProps) {
	const { isLoggedIn, walletAddress, memberSigner: activeAccount } = useWeb3Auth();

	const [karmaBalance, setKarmaBalance] = useState<string>('0');
	const [karmaBalanceEth, setKarmaBalanceEth] = useState<string>('0');
	const [karmaBalanceBase, setKarmaBalanceBase] = useState<string>('0');
	const [ethBalance, setEthBalance] = useState<string>('0');
	const [kusdBalance, setKusdBalance] = useState<string>('0');
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
	const [ethersProvider, setEthersProvider] =
		useState<ethers.Provider | null>(null);
	const [baseProvider, setBaseProvider] =
		useState<ethers.Provider | null>(null);
	const [polygonProvider, setPolygonProvider] =
		useState<ethers.Provider | null>(null); // For KUSD on Polygon

	useEffect(() => {
		// Chain reads use the canonical signer-backed authenticated RPC boundary.
		if (activeAccount) {
			try {
				// Create providers for Ethereum, Base (KARMA), and Polygon (KUSD)
				const ethProvider = new MemberReadProvider(activeAccount, 1);
				const baseProviderNew = new MemberReadProvider(activeAccount, 8453);
				const polygonProviderNew = new MemberReadProvider(activeAccount, 137);

				setEthersProvider(ethProvider);
				setBaseProvider(baseProviderNew);
				setPolygonProvider(polygonProviderNew);
				return () => { ethProvider.destroy(); baseProviderNew.destroy(); polygonProviderNew.destroy(); };
			} catch (error) {
				console.error('Error creating ethers provider:', error);
				setEthersProvider(null);
				setBaseProvider(null);
				setPolygonProvider(null);
			}
		} else {
			setEthersProvider(null);
			setBaseProvider(null);
			setPolygonProvider(null);
		}
	}, [activeAccount]);

	useEffect(() => {
		// Only fetch balances if user is logged in and has a wallet address
		// We need both providers to be available for accurate balance fetching
		if (isLoggedIn && walletAddress && ethersProvider && baseProvider) {
			setIsLoading(true);

			// Fetch KARMA token balance
			const fetchBalances = async () => {
				try {
					// Get combined KARMA balance from both chains
					const karmaBalances = await getCombinedKarmaBalance(
						ethersProvider,
						baseProvider,
						walletAddress
					);

					setKarmaBalanceEth(karmaBalances.ethereum);
					setKarmaBalanceBase(karmaBalances.base);
					setKarmaBalance(karmaBalances.total);

					// Fetch ETH balance using the Ethereum provider
					const nativeBalance = await getEthBalance(
						ethersProvider,
						walletAddress
					);
					setEthBalance(nativeBalance);

					// Fetch KUSD balance using the Polygon provider (KUSD is on Polygon, not Base)
					let kusdFetchedBalance = '0';
					if (polygonProvider) {
						const network = await polygonProvider.getNetwork();
						if (network.chainId === BigInt(137)) {
							kusdFetchedBalance = await getKusdBalance(
								polygonProvider,
								walletAddress
							);
						} else {
							console.warn(
								'[WalletCard] KUSD balance not fetched: Polygon provider not on correct network. Current chain:',
								network.chainId
							);
						}
					}
					setKusdBalance(kusdFetchedBalance);
				} catch (error) {
					console.error(
						'[WalletCard Effect] Error fetching balances:',
						error
					);
				} finally {
					setIsLoading(false);
				}
			};

			fetchBalances();
		} else {
			// If conditions are not met, ensure loading is false if it wasn't already
			if (isLoading) {
				setIsLoading(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoggedIn, walletAddress, ethersProvider, baseProvider]);

	const handleRefreshBalance = async () => {
		if (
			!isLoggedIn ||
			!walletAddress ||
			!ethersProvider ||
			!baseProvider
		)
			return;

		setIsRefreshing(true);
		try {
			// Get combined KARMA balance from both chains
			const refreshedKarmaBalances = await getCombinedKarmaBalance(
				ethersProvider,
				baseProvider,
				walletAddress
			);

			setKarmaBalanceEth(refreshedKarmaBalances.ethereum);
			setKarmaBalanceBase(refreshedKarmaBalances.base);
			setKarmaBalance(refreshedKarmaBalances.total);

			// Fetch ETH balance using the Ethereum provider
			const nativeBalance = await getEthBalance(
				ethersProvider,
				walletAddress
			);
			setEthBalance(nativeBalance);

			// Fetch KUSD balance using the Polygon provider (KUSD is on Polygon, not Base)
			let refreshedKusdBalance = kusdBalance; // Keep existing if provider not available
			if (polygonProvider) {
				const network = await polygonProvider.getNetwork();
				if (network.chainId === BigInt(137)) {
					refreshedKusdBalance = await getKusdBalance(
						polygonProvider,
						walletAddress
					);
				} else {
					console.warn(
						'[WalletCard] KUSD balance not refreshed: Polygon provider not on correct network. Current chain:',
						network.chainId
					);
				}
			}
			setKusdBalance(refreshedKusdBalance);

			toast({
				title: 'Balance Updated',
				description: 'Your wallet balance has been refreshed.',
			});
		} catch (error) {
			console.error('Error refreshing balances:', error);
			toast({
				title: 'Update Failed',
				description: 'Could not refresh wallet balance.',
				variant: 'destructive',
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	if (!isLoggedIn || !walletAddress) {
		return null;
	}

	const karmaContractAddress = import.meta.env
		.VITE_PREDICT_TOKEN_ADDRESS as string;
	const truncatedAddress = karmaContractAddress
		? `${karmaContractAddress.substring(0, 6)}...${karmaContractAddress.substring(karmaContractAddress.length - 4)}`
		: 'N/A';
	const baseWkrmContractAddress = BASE_WKRM_ADDRESS;
	const truncatedBaseWkrmAddress = baseWkrmContractAddress
		? `${baseWkrmContractAddress.substring(0, 6)}...${baseWkrmContractAddress.substring(baseWkrmContractAddress.length - 4)}`
		: 'N/A';

	// Access Pass, Game ID, Watcher Builder, and Watcher Player contract addresses
	const accessPassContractAddress = WATCHER_FOUNDER_PASS_CONTRACT_ADDRESS;
	const truncatedAccessPassAddress = accessPassContractAddress
		? `${accessPassContractAddress.substring(0, 6)}...${accessPassContractAddress.substring(accessPassContractAddress.length - 4)}`
		: 'N/A';
	const oracleEditionContractAddress =
		WATCHER_ORACLE_EDITION_CONTRACT_ADDRESS;
	const truncatedOracleEditionAddress = oracleEditionContractAddress
		? `${oracleEditionContractAddress.substring(0, 6)}...${oracleEditionContractAddress.substring(oracleEditionContractAddress.length - 4)}`
		: 'N/A';
	const gameIdContractAddress = WATCHER_GAME_ID_CONTRACT_ADDRESS;
	const truncatedGameIdAddress = gameIdContractAddress
		? `${gameIdContractAddress.substring(0, 6)}...${gameIdContractAddress.substring(gameIdContractAddress.length - 4)}`
		: 'N/A';
	const watcherBuilderContractAddress = WATCHER_BUILDER_CONTRACT_ADDRESS;
	const truncatedWatcherBuilderAddress = watcherBuilderContractAddress
		? `${watcherBuilderContractAddress.substring(0, 6)}...${watcherBuilderContractAddress.substring(watcherBuilderContractAddress.length - 4)}`
		: 'N/A';
	const watcherPlayerContractAddress = WATCHER_PLAYER_CONTRACT_ADDRESS;
	const truncatedWatcherPlayerAddress = watcherPlayerContractAddress
		? `${watcherPlayerContractAddress.substring(0, 6)}...${watcherPlayerContractAddress.substring(watcherPlayerContractAddress.length - 4)}`
		: 'N/A';

	return (
		<Card
			className={`shadow-[0_0_20px_2px_rgba(236,72,153,0.3)] border-pink-900/50 bg-gradient-to-b from-zinc-900 to-black overflow-hidden ${className}`}
		>
			<CardHeader className=''>
				<div className='flex justify-between items-center'>
					<Wallet className='h-8 w-8 text-primary text-orange-500 mr-2' />
					<CardTitle className='text-2xl font-bold text-pink-500'>
						WALLET
					</CardTitle>

					<Button
						variant='ghost'
						size='lg'
						className='text-pink-500 hover:text-pink-100 hover:bg-pink-800/30 transition-all duration-200'
						onClick={handleRefreshBalance}
						disabled={isRefreshing}
					>
						<RefreshCw
							className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`}
						/>
						{isRefreshing ? 'Updating...' : 'Refresh'}
					</Button>
				</div>
			</CardHeader>

			<CardContent className=''>
				<div className='mb-6'>
					{/* Provider-neutral member wallet status. */}
					<div className='mb-6 p-4 rounded-xl border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)] bg-gradient-to-t from-zinc-900 via-zinc-800 to-black'>
						<div className='relative w-full'>
							<div className='relative z-10 w-full'>
								{activeAccount ? (
									// Connected state - show wallet info
									<div className='flex items-center justify-between w-full p-3 rounded-xl bg-gradient-to-r from-pink-600/20 to-purple-600/20 border border-pink-500/30 hover:border-pink-400/50 transition-all duration-200'>
										<div className='flex min-w-0 items-center space-x-3'>
											{/* WHY no frame: the brand lockup is a WIDE asset (~4:1).
											    Forcing a horizontal logo into a fixed square box
											    squashed it and added a border the owner rejected.
											    Render it at its true aspect ratio instead. */}
											<img
												src='/logo.png'
												alt='Watcher Society'
												width={1073}
												height={264}
												className='h-auto w-full max-w-[160px] shrink-0 object-contain drop-shadow-[0_0_10px_rgba(236,72,153,0.3)]'
											/>
											<div className='flex flex-col'>
												<span className='text-pink-300 text-lg font-medium'>
													{activeAccount.address
														? `${activeAccount.address.slice(0, 6)}...${activeAccount.address.slice(-4)}`
														: 'Connected'}
												</span>
												<span className='text-white/70 text-sm'>
													{formatCurrency(
														karmaBalance,
														0
													)}{' '}
													KARMA
												</span>
											</div>
										</div>
										<div className='text-pink-400'>
											<Wallet className='h-5 w-5' />
										</div>
									</div>
								) : (
									// Signed operations remain unavailable until wallet recovery completes.
									<div className='flex items-center justify-center w-full p-3 rounded-xl bg-gradient-to-r from-pink-600/30 to-purple-600/30 border border-pink-500/50 hover:border-pink-400/70 hover:bg-gradient-to-r hover:from-pink-600/40 hover:to-purple-600/40 transition-all duration-200'>
										<div className='flex items-center space-x-3'>
											<Wallet className='h-5 w-5 text-pink-300' />
											<span className='text-white font-medium'>
												Wallet recovery required
											</span>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
					{isLoading ? (
						<div className='animate-pulse h-16 w-full bg-pink-800/20 rounded-lg'>
							<div className='flex items-center justify-center w-full h-full text-pink-300 animate-pulse'>
								LOADING...
							</div>
						</div>
					) : (
						<div className='flex flex-col items-center p-6 rounded-xl bg-gradient-to-br from-pink-600/30 to-purple-800/30 shadow-[0_0_15px_rgba(236,72,153,0.3)] text-center'>
							<div className='py-4 border-b border-sky-700/20 w-full'>
								<span className='text-4xl font-bold bg-gradient-to-r from-sky-300 to-sky-100 bg-clip-text text-transparent'>
									{formatCurrency(karmaBalanceEth, 2)}
								</span>
								<span className='text-lg text-sky-400 font-semibold mt-1 block'>
									$KARMA (ETH-KARMA)
								</span>
							</div>

							<div className='pt-4 w-full'>
								<span className='text-4xl font-bold bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent'>
									{formatCurrency(karmaBalanceBase, 2)}
								</span>
								<span className='text-lg text-blue-400 font-semibold mt-1 block'>
									$KARMA (BASE-WKRM)
								</span>
							</div>
						</div>
					)}
				</div>

				<div className='mt-10 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-blue-900/30 shadow-[0_0_15px_rgba(59,130,246,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						$KARMA (BASE-WKRM):
					</h2>
					<a
						href={`https://basescan.org/token/${baseWkrmContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-blue-500/50 hover:text-blue-300 transition-colors'
					>
						{truncatedBaseWkrmAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-blue-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						$KARMA (ETH-KARMA):
					</h2>
					<a
						href={`https://etherscan.io/token/${karmaContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-pink-500/50 hover:text-pink-300 transition-colors'
					>
						{truncatedAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-pink-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						Founder Access Pass Contract:
					</h2>
					<a
						href={`https://polygonscan.com/token/${accessPassContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-pink-500/50 hover:text-pink-300 transition-colors'
					>
						{truncatedAccessPassAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-pink-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						Oracle Edition Access Pass Contract:
					</h2>
					<a
						href={`https://polygonscan.com/token/${oracleEditionContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-pink-500/50 hover:text-pink-300 transition-colors'
					>
						{truncatedOracleEditionAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-pink-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						Game ID Contract:
					</h2>
					<a
						href={`https://polygonscan.com/token/${gameIdContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-pink-500/50 hover:text-pink-300 transition-colors'
					>
						{truncatedGameIdAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-pink-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/30 shadow-[0_0_15px_rgba(236,72,153,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						Watcher Builder Contract:
					</h2>
					<a
						href={`https://polygonscan.com/token/${watcherBuilderContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-pink-500/50 hover:text-pink-300 transition-colors'
					>
						{truncatedWatcherBuilderAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-pink-400' />
					</a>
				</div>
				<div className='mt-2 p-4 rounded-xl bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-purple-900/30 shadow-[0_0_15px_rgba(147,51,234,0.15)]'>
					<h2 className='text-lg font-bold text-white mb-2'>
						Watcher Player Contract (Base):
					</h2>
					<a
						href={`https://basescan.org/token/${watcherPlayerContractAddress}`}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center text-sm text-white/80 font-mono underline decoration-purple-500/50 hover:text-purple-300 transition-colors'
					>
						{truncatedWatcherPlayerAddress}
						<ExternalLink className='h-4 w-4 ml-2 text-purple-400' />
					</a>
				</div>

					<ReceiveTokensModal
					isOpen={isReceiveModalOpen}
					onClose={() => setIsReceiveModalOpen(false)}
				/>
			</CardContent>
		</Card>
	);
}
